import { TimeFilterPreset } from '@/types/rebateAnalytics';
import { ALL_MONTHS } from './rebateForecastingUtils';

export type { TimeFilterPreset };

/**
 * Get current month index (0-11)
 */
function getCurrentMonthIndex(): number {
  return new Date().getMonth();
}

/**
 * Convert time filter preset to selectedMonths array
 * Returns undefined for 'all' preset (meaning show all months)
 * Returns special strings ("Current", "Previous Month", "Next Month") that will be resolved by resolveMonth
 */
export function timeFilterPresetToSelectedMonths(
  preset: TimeFilterPreset,
): string[] | undefined {
  const currentMonthIndex = getCurrentMonthIndex();

  switch (preset) {
    case 'all':
      // Return undefined to indicate all months should be shown
      return undefined;
    case 'ytd': {
      // Year to date: January through current month
      // Use month abbreviations for Jan through current month, plus "Current" for current month
      const ytdMonths = ALL_MONTHS.slice(0, currentMonthIndex);
      return [...ytdMonths, 'Current'];
    }
    case 'prev_and_current':
      // Previous month + current month
      return ['Previous Month', 'Current'];
    case 'current':
      // Current month only
      return ['Current'];
    case 'previous':
      // Previous month only
      return ['Previous Month'];
    case 'current_and_next':
      // Current month + next month
      return ['Current', 'Next Month'];
    case 'yoy':
      // Year over year: show all months (similar to 'all')
      return undefined;
    default:
      return undefined;
  }
}

/**
 * Get display label for a time filter preset
 */
export function getTimeFilterPresetLabel(preset: TimeFilterPreset): string {
  switch (preset) {
    case 'ytd':
      return 'YTD';
    case 'all':
      return 'All Time';
    case 'prev_and_current':
      return 'Prev + Current';
    case 'current':
      return 'Current';
    case 'previous':
      return 'Previous';
    case 'current_and_next':
      return 'Current + Next';
    case 'yoy':
      return 'Year Over Year';
    default:
      return 'All Time';
  }
}

/**
 * Get all available time filter presets
 */
/**
 * Check if a preset should show previous year columns
 */
export function shouldShowPreviousYearColumns(
  preset: TimeFilterPreset,
): boolean {
  switch (preset) {
    case 'prev_and_current':
    case 'current':
    case 'previous':
    case 'current_and_next':
    case 'ytd':
    case 'all':
      return false;
    case 'yoy':
      return true;
    default:
      return false;
  }
}

export function getAllTimeFilterPresets(): TimeFilterPreset[] {
  return [
    'ytd',
    'all',
    'prev_and_current',
    'current',
    'previous',
    'current_and_next',
    'yoy',
  ];
}
